import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Role } from '../shared/api';
import {
  Empty, ErrorNote, Modal, PlusIcon, Skeleton, formatDateTime, relativeTime,
  titleise, useToast,
} from '../shared/ui';
import { useAuth } from '../app/auth';

const ROLES: Role[] = [
  'MAKER', 'VERIFIER', 'APPROVER_L1', 'APPROVER_L2', 'APPROVER_L3',
  'FINANCE_OPS', 'AUDITOR', 'ADMIN',
];

interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  role_detail: { label: string; description: string; is_privileged: boolean };
  department: string | null;
  phone: string | null;
  is_active: boolean;
  last_login_at: string | null;
  locked_until: string | null;
  created_at: string;
}

interface Rule {
  id: string;
  level: number;
  role_code: Role;
  min_amount: { minor: number; formatted: string };
  max_amount: { minor: number; formatted: string } | null;
  purpose: string | null;
  quorum: number;
  is_active: boolean;
}

type Tab = 'users' | 'matrix' | 'roles';

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('users');

  const TABS: Array<[Tab, string]> = [
    ['users', 'Users'],
    ['matrix', 'Approval matrix'],
    ['roles', 'Roles & permissions'],
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Administration</div>
          <h1 className="page-title">Access &amp; authority</h1>
          <p className="page-sub">
            Who can act, and who must authorise money leaving the organisation.
            Every change here is audited.
          </p>
        </div>
      </div>

      <div className="row gap-6 row-wrap" style={{ marginBottom: 16 }} role="tablist">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={tab === key}
            className={`btn ${tab === key ? 'btn-primary' : 'btn-ghost'} btn-sm`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'users' && <UsersTab />}
      {tab === 'matrix' && <MatrixTab />}
      {tab === 'roles' && <RolesTab />}
    </>
  );
}

/* ── Users ─────────────────────────────────────────────────────────────── */

function UsersTab() {
  const { user: me } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<AdminUser[]>('/admin/users'),
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/admin/users/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast('User updated', 'ok');
      setEditing(null);
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Update failed', 'bad'),
  });

  const unlock = useMutation({
    mutationFn: (id: string) => api.post(`/admin/users/${id}/unlock`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast('Account unlocked', 'ok');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not unlock', 'bad'),
  });

  if (isLoading) return <Skeleton rows={6} />;
  if (error) return <ErrorNote error={error} />;

  const users = data ?? [];
  const locked = users.filter((u) => u.locked_until && new Date(u.locked_until) > new Date());

  return (
    <>
      <div className="row between row-wrap gap-10" style={{ marginBottom: 14 }}>
        <div className="faint" style={{ fontSize: 13 }}>
          {users.filter((u) => u.is_active).length} active ·{' '}
          {users.filter((u) => !u.is_active).length} deactivated
          {locked.length > 0 && (
            <span style={{ color: 'var(--watch)' }}> · {locked.length} locked out</span>
          )}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
          <PlusIcon /> Add user
        </button>
      </div>

      <div className="card card-flush">
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Department</th>
                <th>Last seen</th>
                <th>Status</th>
                <th style={{ width: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isLocked = !!u.locked_until && new Date(u.locked_until) > new Date();
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.55 }}>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {u.display_name}
                        {isMe && <span className="ghost" style={{ fontSize: 11 }}> (you)</span>}
                      </div>
                    </td>
                    <td className="mono faint" style={{ fontSize: 12 }}>{u.email}</td>
                    <td>
                      <span className={`pill ${u.role_detail.is_privileged ? 'pill-live' : 'pill-info'}`}>
                        {u.role_detail.label}
                      </span>
                    </td>
                    <td className="faint" style={{ fontSize: 12.5 }}>{u.department ?? '—'}</td>
                    <td className="ghost" style={{ fontSize: 11.5 }}>
                      {u.last_login_at ? relativeTime(u.last_login_at) : 'Never'}
                    </td>
                    <td>
                      {isLocked ? (
                        <span className="pill pill-bad">Locked</span>
                      ) : u.is_active ? (
                        <span className="pill pill-ok">Active</span>
                      ) : (
                        <span className="pill pill-draft">Deactivated</span>
                      )}
                    </td>
                    <td>
                      <div className="row gap-6">
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditing(u)}>
                          Edit
                        </button>
                        {isLocked && (
                          <button
                            className="btn btn-ok btn-sm"
                            onClick={() => unlock.mutate(u.id)}
                            disabled={unlock.isPending}
                          >
                            Unlock
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {creating && <CreateUser onClose={() => setCreating(false)} />}
      {editing && (
        <EditUser
          user={editing}
          isSelf={editing.id === me?.id}
          onClose={() => setEditing(null)}
          onSave={(body) => update.mutate({ id: editing.id, body })}
          saving={update.isPending}
        />
      )}
    </>
  );
}

function CreateUser({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('MAKER');
  const [dept, setDept] = useState('');
  const [password, setPassword] = useState('');

  const create = useMutation({
    mutationFn: () =>
      api.post('/admin/users', {
        email, display_name: name, role, department: dept || null, password,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast(`${name} created`, 'ok');
      onClose();
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not create', 'bad'),
  });

  const strong =
    password.length >= 12 &&
    [/[a-z]/, /[A-Z]/, /\d/, /[^a-zA-Z0-9]/].filter((r) => r.test(password)).length >= 3;
  const valid = email.includes('@') && name && strong;

  return (
    <Modal title="Add a user" onClose={onClose}>
      <div className="col gap-14">
        <div className="field">
          <label className="label" htmlFor="ne">Email</label>
          <input id="ne" className="input" type="email" value={email}
                 onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label className="label" htmlFor="nn">Full name</label>
          <input id="nn" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label className="label" htmlFor="nr">Role</label>
          <select id="nr" className="select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => <option key={r} value={r}>{titleise(r)}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="nd">Department</label>
          <input id="nd" className="input" value={dept} onChange={(e) => setDept(e.target.value)} />
        </div>
        <div className="field">
          <label className="label" htmlFor="np">Initial password</label>
          <input id="np" className="input" type="text" value={password}
                 onChange={(e) => setPassword(e.target.value)}
                 placeholder="At least 12 characters" />
          {password.length > 0 && !strong && (
            <div className="ghost" style={{ fontSize: 11.5 }}>
              Needs 12+ characters and three of: lowercase, uppercase, digits, symbols.
            </div>
          )}
        </div>

        {role === 'ADMIN' && (
          <div className="alert alert-warn">
            Administrators can manage users and change the approval matrix — the rules
            governing who authorises payments. Grant this sparingly.
          </div>
        )}

        <div className="row gap-6">
          <button className="btn btn-ghost grow" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary grow"
            disabled={!valid || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EditUser({
  user, isSelf, onClose, onSave, saving,
}: {
  user: AdminUser;
  isSelf: boolean;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const toast = useToast();
  const [role, setRole] = useState<Role>(user.role);
  const [dept, setDept] = useState(user.department ?? '');
  const [active, setActive] = useState(user.is_active);
  const [resetting, setResetting] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const reset = useMutation({
    mutationFn: () =>
      api.post(`/admin/users/${user.id}/reset-password`, { new_password: newPassword }),
    onSuccess: () => {
      toast(`Password reset. ${user.display_name} has been signed out.`, 'ok');
      setResetting(false);
      setNewPassword('');
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Reset failed', 'bad'),
  });

  const roleChanged = role !== user.role;

  return (
    <Modal title={user.display_name} onClose={onClose}>
      <div className="col gap-14">
        <div className="row between">
          <span className="mono faint" style={{ fontSize: 12.5 }}>{user.email}</span>
          <span className="ghost" style={{ fontSize: 11.5 }}>
            Joined {formatDateTime(user.created_at)}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="er">Role</label>
          <select
            id="er" className="select" value={role} disabled={isSelf}
            onChange={(e) => setRole(e.target.value as Role)}
          >
            {ROLES.map((r) => <option key={r} value={r}>{titleise(r)}</option>)}
          </select>
          {isSelf && (
            <div className="ghost" style={{ fontSize: 11.5 }}>
              You cannot change your own role. Another administrator must do it.
            </div>
          )}
        </div>

        {roleChanged && !isSelf && (
          <div className="alert alert-warn">
            Changing the role signs {user.display_name} out immediately and applies the
            new permissions on their next sign-in.
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="ed">Department</label>
          <input id="ed" className="input" value={dept} onChange={(e) => setDept(e.target.value)} />
        </div>

        <div className="row between" style={{ padding: '10px 0' }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 500 }}>Account active</div>
            <div className="ghost" style={{ fontSize: 11.5 }}>
              Deactivating prevents sign-in immediately
            </div>
          </div>
          <button
            className={`btn btn-sm ${active ? 'btn-ok' : 'btn-ghost'}`}
            disabled={isSelf}
            onClick={() => setActive((v) => !v)}
          >
            {active ? 'Active' : 'Deactivated'}
          </button>
        </div>

        <hr className="hr" />

        {resetting ? (
          <div className="col gap-10">
            <div className="field">
              <label className="label" htmlFor="rp">New password</label>
              <input
                id="rp" className="input" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 12 characters" autoFocus
              />
            </div>
            <div className="row gap-6">
              <button className="btn btn-ghost grow" onClick={() => setResetting(false)}>
                Cancel
              </button>
              <button
                className="btn btn-bad grow"
                disabled={newPassword.length < 12 || reset.isPending}
                onClick={() => reset.mutate()}
              >
                {reset.isPending ? 'Resetting…' : 'Reset password'}
              </button>
            </div>
          </div>
        ) : (
          <button className="btn btn-ghost btn-sm" onClick={() => setResetting(true)}>
            Reset password
          </button>
        )}

        <div className="row gap-6">
          <button className="btn btn-ghost grow" onClick={onClose}>Close</button>
          <button
            className="btn btn-primary grow"
            disabled={saving}
            onClick={() => onSave({ role, department: dept || null, is_active: active })}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ── Approval matrix ───────────────────────────────────────────────────── */

function MatrixTab() {
  const toast = useToast();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['approval-rules'],
    queryFn: () => api.get<Rule[]>('/admin/approval-rules'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/admin/approval-rules/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-rules'] });
      toast('Rule removed', 'ok');
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not remove', 'bad'),
  });

  if (isLoading) return <Skeleton rows={5} />;
  const rules = data ?? [];

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="faint" style={{ fontSize: 13, lineHeight: 1.65 }}>
          When an order is submitted, every rule matching its amount and purpose becomes
          a required approval step, in level order. Orders already in approval keep the
          chain they were given at submission — changes here affect new submissions only.
        </div>
      </div>

      <div className="row between" style={{ marginBottom: 12 }}>
        <span className="faint" style={{ fontSize: 13 }}>
          {rules.filter((r) => r.is_active).length} active rule(s)
        </span>
        <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
          <PlusIcon /> Add rule
        </button>
      </div>

      {rules.length === 0 ? (
        <div className="card">
          <Empty
            title="No approval rules"
            hint="Without at least one rule, no payment order can be submitted."
            action={<button className="btn btn-primary" onClick={() => setAdding(true)}>Add the first rule</button>}
          />
        </div>
      ) : (
        <div className="card card-flush">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Level</th>
                  <th>Approving role</th>
                  <th className="right">Applies from</th>
                  <th className="right">Up to</th>
                  <th>Purpose</th>
                  <th>Quorum</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} style={{ opacity: r.is_active ? 1 : 0.5 }}>
                    <td>
                      <span className="pill pill-live">L{r.level}</span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{titleise(r.role_code)}</td>
                    <td className="num">{r.min_amount.formatted}</td>
                    <td className="num faint">{r.max_amount?.formatted ?? 'No limit'}</td>
                    <td className="faint" style={{ fontSize: 12.5 }}>
                      {r.purpose ? titleise(r.purpose) : 'All purposes'}
                    </td>
                    <td className="mono faint">{r.quorum}</td>
                    <td>
                      <button
                        className="btn btn-bad btn-sm"
                        onClick={() => remove.mutate(r.id)}
                        disabled={remove.isPending}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {adding && <AddRule onClose={() => setAdding(false)} />}
    </>
  );
}

function AddRule({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [level, setLevel] = useState('1');
  const [role, setRole] = useState<Role>('VERIFIER');
  const [min, setMin] = useState('0');
  const [max, setMax] = useState('');
  const [quorum, setQuorum] = useState('1');

  const create = useMutation({
    mutationFn: () =>
      api.post('/admin/approval-rules', {
        level: Number(level),
        role_code: role,
        min_amount_minor: Math.round(Number(min.replace(/[^\d.]/g, '') || '0') * 100),
        max_amount_minor: max ? Math.round(Number(max.replace(/[^\d.]/g, '')) * 100) : null,
        quorum: Number(quorum),
        is_active: true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['approval-rules'] });
      toast('Rule added', 'ok');
      onClose();
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not add', 'bad'),
  });

  return (
    <Modal title="Add an approval rule" onClose={onClose}>
      <div className="col gap-14">
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div className="field">
            <label className="label" htmlFor="rl">Level</label>
            <select id="rl" className="select" value={level} onChange={(e) => setLevel(e.target.value)}>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Level {n}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="rr">Approving role</label>
            <select id="rr" className="select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.filter((r) => r !== 'MAKER' && r !== 'AUDITOR').map((r) => (
                <option key={r} value={r}>{titleise(r)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div className="field">
            <label className="label" htmlFor="rmin">Applies from (₹)</label>
            <input id="rmin" className="input num" value={min} inputMode="decimal"
                   onChange={(e) => setMin(e.target.value)} />
          </div>
          <div className="field">
            <label className="label" htmlFor="rmax">Up to (₹, optional)</label>
            <input id="rmax" className="input num" value={max} inputMode="decimal"
                   placeholder="No limit" onChange={(e) => setMax(e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="rq">Quorum</label>
          <select id="rq" className="select" value={quorum} onChange={(e) => setQuorum(e.target.value)}>
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>{n} approver{n > 1 ? 's' : ''} at this level</option>
            ))}
          </select>
        </div>

        <div className="hint" style={{ borderStyle: 'solid' }}>
          Levels are sequential: level 2 cannot act until level 1 has decided.
        </div>

        <div className="row gap-6">
          <button className="btn btn-ghost grow" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary grow" disabled={create.isPending}
                  onClick={() => create.mutate()}>
            {create.isPending ? 'Adding…' : 'Add rule'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ── Roles ─────────────────────────────────────────────────────────────── */

interface RoleRow {
  code: string;
  label: string;
  description: string;
  approval_level: number | null;
  permission_count: number;
  permissions: string[];
  is_privileged: boolean;
}

function RolesTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['role-catalogue'],
    queryFn: () =>
      api.get<{
        roles: RoleRow[];
        permissions: Array<{ code: string; label: string; group: string; sensitive: boolean }>;
        groups: string[];
      }>('/admin/roles'),
  });

  if (isLoading) return <Skeleton rows={6} />;
  if (!data) return null;

  return (
    <>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="faint" style={{ fontSize: 13, lineHeight: 1.65 }}>
          This is the delegation of authority in full. Roles are fixed in code rather
          than editable at runtime — a payments system where permissions can be
          reassigned through the interface is one bad session away from a problem.
        </div>
      </div>

      <div className="card card-flush" style={{ marginBottom: 14 }}>
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ minWidth: 210 }}>Permission</th>
                {data.roles.map((r) => (
                  <th key={r.code} style={{ textAlign: 'center', minWidth: 62 }}>
                    <span style={{ fontSize: 9.5 }}>{r.label.replace(/ /g, '\u00a0')}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.groups.map((group) => (
                <>
                  <tr key={group}>
                    <td
                      colSpan={data.roles.length + 1}
                      className="eyebrow"
                      style={{ background: 'var(--ink)', paddingTop: 12 }}
                    >
                      {group}
                    </td>
                  </tr>
                  {data.permissions
                    .filter((p) => p.group === group)
                    .map((p) => (
                      <tr key={p.code}>
                        <td style={{ fontSize: 12.5 }}>
                          {p.label}
                          {p.sensitive && (
                            <span className="pill pill-wait" style={{ marginLeft: 7, fontSize: 9.5 }}>
                              Sensitive
                            </span>
                          )}
                        </td>
                        {data.roles.map((r) => (
                          <td key={r.code} style={{ textAlign: 'center' }}>
                            {r.permissions.includes(p.code) ? (
                              <span style={{ color: 'var(--verify)' }} aria-label="granted">✓</span>
                            ) : (
                              <span className="ghost" aria-label="not granted">·</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))' }}>
        {data.roles.map((r) => (
          <div className="card" key={r.code}>
            <div className="row between" style={{ marginBottom: 8 }}>
              <span className={`pill ${r.is_privileged ? 'pill-live' : 'pill-info'}`}>{r.label}</span>
              <span className="mono ghost" style={{ fontSize: 11 }}>
                {r.permission_count} perms
              </span>
            </div>
            <div className="faint" style={{ fontSize: 12.5, lineHeight: 1.6 }}>{r.description}</div>
            {r.approval_level && (
              <div className="ghost" style={{ fontSize: 11.5, marginTop: 8 }}>
                Approves at level {r.approval_level}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
